import React, { useState, useCallback, useEffect } from 'react';
import { AlertTriangle, Lock, Moon, Sun, RefreshCw } from 'lucide-react';
import { getBillingPortalUrl } from '../api/paymentsService';
import { getUser } from '../api/userDataService';
import { apiCache } from '../utils/apiCache';
import useResetOnRestore from '../hooks/useResetOnRestore';
import { getStoredTheme, toggleTheme } from '../utils/theme';

// Fields the lock decision is made from — mirrors the merge in App.js.
const SUBSCRIPTION_FIELDS = [
  'subscriptionStatus',
  'subscriptionExpiry',
  'pastDueSince',
  'cancelAtPeriodEnd',
  'hasBilling',
  'subscriptionPlan',
  'accountStatus',
  'accountType',
  'accessState',
];

function AccountLocked({ onLogout, onAccessRestored }) {
  const paymentUrl = process.env.REACT_APP_PAYMENT_URL || 'https://routes-app.com/subscribe';

  // A returning customer whose renewal payment failed (past_due) has a Stripe customer
  // record, so we can send them to the billing portal to fix their card instead of a
  // fresh checkout. Everyone else (never subscribed / fully cancelled) re-subscribes.
  let cachedUser = {};
  try { cachedUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch { /* ignore */ }
  const isPastDue = cachedUser.subscriptionStatus === 'past_due';
  const canManageBilling = isPastDue && !!cachedUser.hasBilling;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // This screen replaces the whole app, TopBar included, so it carries its own
  // theme toggle — otherwise a dark-mode user has no way to change it here.
  const [theme, setTheme] = useState(getStoredTheme);

  const [rechecking, setRechecking] = useState(false);
  const [recheckedStillLocked, setRecheckedStillLocked] = useState(false);

  // Re-read access from the server. Checkout happens off-site, so the cached
  // user still says locked when they come back; without this the only way out
  // is to log in again.
  const recheckAccess = useCallback(async (manual) => {
    if (manual) {
      setRechecking(true);
      setRecheckedStillLocked(false);
    }
    try {
      // Bypass the 5-minute cache — a stale hit here would report locked.
      apiCache.invalidate('user-data');
      const response = await getUser();
      const fresh = response?.result?.user;
      if (!fresh) return;

      let cached = {};
      try { cached = JSON.parse(localStorage.getItem('user') || '{}'); } catch { /* ignore */ }
      const merged = { ...cached };
      SUBSCRIPTION_FIELDS.forEach((f) => { merged[f] = fresh[f]; });
      localStorage.setItem('user', JSON.stringify(merged));

      const stillLocked = merged.accessState
        ? merged.accessState === 'locked'
        : merged.subscriptionStatus !== 'active';

      if (!stillLocked && onAccessRestored) onAccessRestored(merged);
      else if (manual) setRecheckedStillLocked(true);
    } catch {
      if (manual) setRecheckedStillLocked(true);
    } finally {
      if (manual) setRechecking(false);
    }
  }, [onAccessRestored]);

  // On arrival, and again when the page is restored after checkout.
  useEffect(() => { recheckAccess(false); }, [recheckAccess]);

  // Returning from the Stripe portal restores this page from bfcache with
  // `loading` still true, leaving the button stuck on "Opening…".
  useResetOnRestore(useCallback(() => {
    setLoading(false);
    recheckAccess(false);
  }, [recheckAccess]));

  const handleManageBilling = async () => {
    setLoading(true);
    setError('');
    try {
      const url = await getBillingPortalUrl();
      if (url) { window.location.href = url; return; }
      setError('Could not open the billing portal. Please try again.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Could not open the billing portal. Please try again.');
    }
    setLoading(false);
  };

  // Amber for a failed payment (recoverable, their card just needs fixing);
  // neutral accent for an ended subscription (a deliberate state, not a fault).
  const tint = isPastDue
    ? { wash: 'var(--warn-wash)', ink: 'var(--warn-ink)' }
    : { wash: 'var(--accent-wash, var(--paper-2))', ink: 'var(--accent)' };

  const Icon = isPastDue ? AlertTriangle : Lock;

  return (
    <div
      className="rd"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper)',
        padding: '2rem 1.25rem',
        position: 'relative',
      }}
    >
      <button
        type="button"
        className="btn btn-ghost btn-icon"
        onClick={() => setTheme(toggleTheme())}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        style={{ position: 'absolute', top: 16, right: 16 }}
      >
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 440,
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Tinted band, so the lock screen reads as the same family as the
            past-due banner rather than an unrelated page. */}
        <div
          style={{
            background: tint.wash,
            borderBottom: `1px solid ${tint.ink}`,
            padding: '1.5rem clamp(1.25rem, 6vw, 2rem)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.625rem',
          }}
        >
          <Icon size={26} strokeWidth={1.9} color={tint.ink} aria-hidden="true" />
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: tint.ink,
            }}
          >
            {isPastDue ? 'Payment failed' : 'Subscription ended'}
          </span>
        </div>

        <div style={{ padding: '1.75rem clamp(1.25rem, 6vw, 2rem) 2rem' }}>
        <h1
          style={{
            fontSize: '1.375rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'var(--ink)',
            margin: '0 0 0.625rem',
          }}
        >
          {isPastDue ? 'Your account is on hold' : 'Your account is inactive'}
        </h1>

        <p
          style={{
            color: 'var(--ink-2)',
            fontSize: '0.9375rem',
            lineHeight: 1.6,
            margin: '0 0 1.75rem',
          }}
        >
          {isPastDue
            ? 'Your last subscription payment didn’t go through, so your account is on hold. Update your payment method to restore access to your profile.'
            : 'Your account is currently inactive. Subscribe to keep your profile active, stay visible to employers, and continue accessing all your Routes content.'}
        </p>

        {error && (
          <p
            role="alert"
            style={{
              color: 'var(--danger)',
              fontSize: '0.85rem',
              lineHeight: 1.5,
              margin: '0 0 0.875rem',
            }}
          >
            {error}
          </p>
        )}

        {canManageBilling ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleManageBilling}
            disabled={loading}
            style={{ width: '100%', justifyContent: 'center', padding: '12px 16px', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Opening…' : 'Update payment method'}
          </button>
        ) : (
          <a
            href={paymentUrl}
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '12px 16px', textDecoration: 'none' }}
          >
            Subscribe to continue
          </a>
        )}

        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => recheckAccess(true)}
          disabled={rechecking}
          style={{ width: '100%', justifyContent: 'center', padding: '10px 16px', marginTop: '0.75rem' }}
        >
          <RefreshCw size={16} />
          {rechecking ? 'Checking…' : "I've already paid, check again"}
        </button>

        <p
          style={{
            fontSize: '0.8125rem',
            lineHeight: 1.5,
            color: 'var(--ink-3)',
            margin: '1rem 0 0',
          }}
        >
          {recheckedStillLocked
            ? 'Your payment has not come through yet. It can take a few moments, so please try again shortly.'
            : canManageBilling
              ? 'Once your payment succeeds your account is reactivated automatically.'
              : 'After payment your account is reactivated automatically.'}
        </p>

        <div style={{ borderTop: '1px solid var(--line)', margin: '1.5rem 0 0', paddingTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onLogout}
          >
            Log out
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

export default AccountLocked;
